import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import BrandLogo from "@/components/BrandLogo";
import { ChevronLeft, Loader2 } from "lucide-react";

/**
 * LegalPage — single page that renders either the Privacy Policy or Terms of
 * Use sourced from the same `/settings/landing` doc the Landing footer used to
 * render inline. URL: /legal/privacy or /legal/terms (tab switcher across the top).
 */
const TABS = [
  { id: "privacy", label: "Privacy Policy", field: "privacy_policy" },
  { id: "terms", label: "Terms of Use", field: "terms_of_use" },
];

export default function LegalPage() {
  const { tab } = useParams();
  const nav = useNavigate();
  const active = TABS.find((t) => t.id === tab);
  const [lp, setLp] = useState(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/landing`)
      .then((r) => { if (alive) setLp((r.data && r.data.landing) || {}); })
      .catch(() => { if (alive) setLp({}); });
    return () => { alive = false; };
  }, []);

  if (!active) return <Navigate to="/legal/privacy" replace />;

  const body = lp ? (lp[active.field] || "").toString() : "";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100" data-testid="legal-page">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
            <BrandLogo size="sm" />
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav(-1)}
            data-testid="legal-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Legal</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
          Our policies for using PrepMasters.
        </p>

        <div
          role="tablist"
          aria-label="Legal sections"
          className="mt-6 inline-flex p-1 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
        >
          {TABS.map((t) => {
            const isActive = t.id === active.id;
            return (
              <Link
                key={t.id}
                to={`/legal/${t.id}`}
                role="tab"
                aria-selected={isActive}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-white dark:bg-slate-900 text-[#1E3A8A] dark:text-blue-300 shadow-sm"
                    : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                }`}
                data-testid={`legal-tab-${t.id}`}
              >
                {t.label}
              </Link>
            );
          })}
        </div>

        <Card className="mt-6 p-6 sm:p-8 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <h2 className="font-display text-2xl font-bold mb-4">{active.label}</h2>
          {lp === null ? (
            <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : body.trim() ? (
            <pre
              className="whitespace-pre-wrap break-words font-sans text-[15px] leading-relaxed text-slate-700 dark:text-slate-200"
              data-testid={`legal-body-${active.id}`}
            >
              {body}
            </pre>
          ) : (
            <p className="text-slate-500 italic">No content has been published yet.</p>
          )}
        </Card>

        <p className="mt-8 text-xs text-slate-500 dark:text-slate-400">
          Questions about this policy? Contact <a className="underline hover:text-[#1E3A8A] dark:hover:text-blue-300" href="mailto:support@prepmasters.ph">support@prepmasters.ph</a>.
        </p>
      </main>
    </div>
  );
}
